/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { discovery } from "dc-core";
import { MODIFIER_KEYS, KEYWORDS_TO_REMOVE, KEYS_TO_DISPLAY_LABELS } from "shortcutManager/constants";
import { contextMenuManager } from "contextMenuManager";

export const getFormattedShortcutExpr = event => {
  let modifiers = "";
  if (event.altKey) modifiers += "alt_";
  if (event.ctrlKey) modifiers += "ctrl_";
  if (event.metaKey) modifiers += "meta_";
  if (event.shiftKey) modifiers += "shift_";

  return `${modifiers}${sanitiseEventCode(event.code.toLowerCase())}`;
};

export const formatShortcutExpr = unformattedShortcutExpr => {
  const keyNames = unformattedShortcutExpr
    .toLowerCase()
    .split("+")
    .map(sanitiseModifierKeys)
    .sort();

  const modifiers = keyNames.filter(isModifier).join("_");
  const key = keyNames.filter(key => !isModifier(key)).pop();

  return modifiers ? (key ? `${modifiers}_${key}` : null) : key;
};

export const getDisplayLabel = key => {
  const sanitisedKey = sanitiseModifierKeys(key.toLowerCase());
  if (KEYS_TO_DISPLAY_LABELS[sanitisedKey]) {
    return KEYS_TO_DISPLAY_LABELS[sanitisedKey];
  }

  return key;
};

const sanitiseEventCode = code => {
  // Remove some keyboards like key, digit
  let result = code;
  for (const keyword of KEYWORDS_TO_REMOVE) {
    result = result.replace(keyword, "");
  }
  return result;
};

const sanitiseModifierKeys = key => {
  if (key === "command" || key === "cmd") return "meta";
  if (key === "control") return "ctrl";
  return key;
};

const isModifier = key => MODIFIER_KEYS.includes(key);

const callDropinMethod = (dropinId, methodName, opts) => discovery.callBootstrapMethod(dropinId, methodName, opts);

const callInitializeAction = (verbId, opts = {}) => callDropinMethod(verbId, "initializeAction", opts);

export const callInitializeActionForVerb = verbId => callInitializeAction(verbId, { context: "viewerKeyboardEvent" });

export const callExecuteAction = (verbId, opts = {}) => callDropinMethod(verbId, "executeAction", opts);

export const getEventHandlerForVerb = (verbId, stores) =>
  typeof verbId === "string" &&
  ((event, opts) => {
    contextMenuManager.clearMenuItems(stores);
    callInitializeActionForVerb(verbId).then(() =>
      callExecuteAction(verbId, { ...opts, event, context: "ViewerKeyboardShortcut" })
    );
    return { shouldPreventDefault: true };
  });
